<template>
  <footer class="site-footer">
    <div class="background-elements">
      <div class="grid-overlay"></div>
    </div>

    <div class="content-container">
      <div class="footer-grid">
        <!-- Company Info -->
        <div class="brand-section fade-in" data-delay="100">
          <div class="brand-tag">
            <div class="tag-dot"></div>
            <span>VisionLab Studio</span>
          </div>
          <h2 class="brand">Transforming Ideas into Digital Solutions</h2>
          <p class="brand-description">
            We create sophisticated digital solutions that empower businesses
            through innovative technology. From enterprise applications to
            custom development, we bring your vision to life.
          </p>
        </div>

        <!-- Empty middle column for spacing -->
        <div class="spacer-section"></div>

        <!-- Contact -->
        <div class="contact-section fade-in" data-delay="500">
          <h3>Get in Touch</h3>
          <address>
            <a href="tel:+14244734444" class="contact-link">
              <svg class="icon" viewBox="0 0 24 24">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
                />
              </svg>
              (424) 473-4444
            </a>
            <a href="mailto:s.michael@vision-lab.studio" class="contact-link">
              <svg class="icon" viewBox="0 0 24 24">
                <path
                  d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                />
                <path d="m22 6-10 7L2 6" />
              </svg>
              s.michael@vision-lab.studio
            </a>
            <p class="location">
              <svg class="icon" viewBox="0 0 24 24">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Los Angeles, CA
            </p>
          </address>
        </div>
      </div>

      <div class="footer-bottom fade-in" data-delay="700">
        <p>© 2025 VisionLab Studio. All rights reserved.</p>
        <nav class="legal-nav">
          <router-link to="/privacy">Privacy Policy</router-link>
        </nav>
      </div>
    </div>
  </footer>
</template>

<script>
import { onMounted } from "vue";

export default {
  name: "ModernFooter",
  setup() {
    onMounted(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const delay = entry.target.dataset.delay || 0;
              setTimeout(() => {
                entry.target.classList.add("visible");
              }, delay);
            }
          });
        },
        { threshold: 0.1 }
      );

      document.querySelectorAll(".fade-in").forEach((el) => {
        observer.observe(el);
      });

      return () => observer.disconnect();
    });
  },
};
</script>

<style lang="scss" scoped>
.site-footer {
  --primary-color: #6366f1;
  --accent-color: #818cf8;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;
  --border-color: rgba(99, 102, 241, 0.2);

  position: relative;
  background-color: #000000;
  color: var(--text-primary);
  padding: 6rem 0 2rem;
  overflow: hidden;
}

.background-elements {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.gradient-sphere {
  position: absolute;
  bottom: -40%;
  left: -20%;
  width: 80vh;
  height: 80vh;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.2) 0%,
    rgba(99, 102, 241, 0.1) 40%,
    transparent 70%
  );
  border-radius: 50%;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(#3d73ff08 1px, transparent 1px),
    linear-gradient(90deg, #3d73ff08 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
}

.content-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
}

.spacer-section {
  /* Пустая колонка для создания пространства */
}

.brand-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid var(--border-color);
  border-radius: 100px;
  margin-bottom: 2rem;

  .tag-dot {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.05em;
  }
}

.brand {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--text-primary), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
}

.brand-description {
  color: var(--text-secondary);
  line-height: 1.7;
  max-width: 500px;
}

h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
}

.footer-nav {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.nav-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.3s ease;
  width: fit-content;

  &:hover {
    color: var(--accent-color);
  }
}

address {
  font-style: normal;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-link,
.location {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.3s ease;

  .icon {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  &:hover {
    color: var(--accent-color);
  }
}

.footer-bottom {
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    color: var(--text-secondary);
  }

  .legal-nav {
    display: flex;
    gap: 2rem;

    a {
      color: var(--text-secondary);
      text-decoration: none;
      transition: all 0.3s ease;

      &:hover {
        color: var(--accent-color);
      }
    }
  }
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 1024px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }

  .brand-section {
    grid-column: 1 / -1;
  }

  .spacer-section {
    display: none;
  }

  .brand {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .site-footer {
    padding: 4rem 0 2rem;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;

    .legal-nav {
      justify-content: center;
    }
  }
}
</style>
